import { useCallback, useEffect, useRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  readTableGuideVisible,
  tableCameraGuideModel,
  writeTableGuideVisible,
} from "./tableCameraGuideModel";
import "./TableCameraGuide.css";

function linePath(a, b) {
  return `M${a.x.toFixed(2)} ${a.y.toFixed(2)} L${b.x.toFixed(2)} ${b.y.toFixed(2)}`;
}

function polyPath(points) {
  return `${points
    .map((p, index) => `${index === 0 ? "M" : "L"}${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(" ")} Z`;
}

function lerp(a, b, t) {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

export function useTableCameraGuide() {
  const [visible, setVisible] = useState(() => readTableGuideVisible());
  const toggle = useCallback(() => {
    setVisible((prev) => {
      const next = !prev;
      writeTableGuideVisible(next);
      return next;
    });
  }, []);
  return [visible, toggle];
}

/**
 * Transparent table template on the live preview. The white lines are the
 * table; the cyan corners stay visible once the real white lines overlap them.
 */
export function TableCameraGuide({
  position = "spectator",
  players = false,
  show = true,
  placement = "preview",
  onToggle,
}) {
  const { t } = useTranslation();
  const frameRef = useRef(null);
  const [landscape, setLandscape] = useState(false);
  useEffect(() => {
    const node = frameRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const update = () => {
      const { width, height } = node.getBoundingClientRect();
      setLandscape(width > height * 1.2);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const model = tableCameraGuideModel(position, { players, landscape });
  const [netFar, netNear] = model.net;
  const [topFar, topNear] = model.netTop;
  const mesh = [1, 2, 3, 4, 5, 6].map((step) => {
    const t = step / 7;
    return [lerp(netFar, netNear, t), lerp(topFar, topNear, t)];
  });

  return (
    <div
      ref={frameRef}
      className={`cm-table-guide${
        placement === "settings" ? " cm-table-guide--settings" : ""
      }`}
    >
      {show ? (
        <>
          <svg
            className="cm-table-guide__svg"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            data-testid="table-guide-outline"
            aria-hidden
          >
            <path className="cm-table-guide__band" d={polyPath(model.surface)} />
            <path className="cm-table-guide__surface" d={polyPath(model.playing)} />
            {model.center.map(([a, b], index) => (
              <path
                key={`center-${index}`}
                className="cm-table-guide__center"
                d={linePath(a, b)}
              />
            ))}
            <path
              className="cm-table-guide__net"
              d={polyPath([netFar, netNear, topNear, topFar])}
            />
            {mesh.map(([a, b], index) => (
              <path
                key={`mesh-${index}`}
                className="cm-table-guide__mesh"
                d={linePath(a, b)}
              />
            ))}
            <path className="cm-table-guide__cord" d={linePath(topFar, topNear)} />
            {model.ticks.map(([a, b], index) => (
              <path
                key={`tick-${index}`}
                className="cm-table-guide__tick"
                d={linePath(a, b)}
              />
            ))}
          </svg>
          {model.scoreboard ? (
            <div
              className="cm-table-guide__score"
              style={{
                left: `${model.scoreboard.x}%`,
                top: `${model.scoreboard.y}%`,
                width: `${model.scoreboard.w}%`,
                height: `${model.scoreboard.h}%`,
              }}
              aria-hidden
            >
              <span>{t("cmTableGuideScoreboard", "Scoreboard")}</span>
            </div>
          ) : null}
          <p className="cm-table-guide__hint">{t(model.hintKey, model.hint)}</p>
        </>
      ) : null}
      <button
        type="button"
        className={`cm-table-guide__toggle${show ? " is-on" : ""}`}
        aria-pressed={show}
        aria-label={
          show
            ? t("cmTableGuideHide", "Hide table guide")
            : t("cmTableGuideShow", "Show table guide")
        }
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation();
          onToggle?.();
        }}
      >
        {show ? (
          <Eye size={14} strokeWidth={2.3} aria-hidden />
        ) : (
          <EyeOff size={14} strokeWidth={2.3} aria-hidden />
        )}
        <span>{t("cmTableGuide", "Guide")}</span>
      </button>
    </div>
  );
}
